import {
  Box,
  Button,
  Chip,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { options } from "config";
import { useCvStepNavigation } from "../../../contexts/CvStepNavigationContext";
import { usePatchCvMutation } from "../../../lib/cvsSlice";

const Roles = ({ roles, cvId }) => {
  // Flatten all selected role values from the first roles subdoc (if any)
  const existingDoc = Array.isArray(roles) && roles.length > 0 ? roles[0] : null;

  const [selectedRoles, setSelectedRoles] = useState(
    existingDoc?.roles ?? []
  );
  const [otherRole, setOtherRole] = useState(existingDoc?.otherRole ?? "");
  const [isSaving, setIsSaving] = useState(false);
  const isDirty = useRef(false);

  const [patchCv] = usePatchCvMutation();
  const { goToNextStep, goToPrevStep } = useCvStepNavigation();

  // Only sync from props when the user hasn't touched anything yet.
  // CvDetails polls every second creating new array refs — without this guard
  // every poll would wipe the user's unsaved chip selections.
  useEffect(() => {
    if (isDirty.current) return;
    const doc = Array.isArray(roles) && roles.length > 0 ? roles[0] : null;
    setSelectedRoles(doc?.roles ?? []);
    setOtherRole(doc?.otherRole ?? "");
  }, [roles, cvId]);

  const toggleRole = (value) => {
    isDirty.current = true;
    setSelectedRoles((prev) =>
      prev.includes(value) ? prev.filter((r) => r !== value) : [...prev, value]
    );
  };

  const handleNext = async () => {
    if (selectedRoles.length === 0) {
      toast.info("Please select at least one role");
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        roles: selectedRoles,
        otherRole: selectedRoles.includes("other") ? otherRole : "",
      };

      if (existingDoc?._id) {
        // Update existing subdocument
        await patchCv({
          cvId,
          roles: [{ id: existingDoc._id, ...payload }],
        }).unwrap();
      } else {
        // Add new subdocument
        await patchCv({
          cvId,
          roles: [payload],
        }).unwrap();
      }

      goToNextStep();
    } catch (err) {
      toast.error(`Failed to save: ${err?.data?.message ?? err.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const showOtherField = selectedRoles.includes("other");

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Role chips */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
        {options.map((opt) => {
          const isSelected = selectedRoles.includes(opt.value);
          return (
            <Chip
              key={opt.value}
              label={opt.label}
              onClick={() => toggleRole(opt.value)}
              variant={isSelected ? "filled" : "outlined"}
              sx={{
                fontSize: "13px",
                fontFamily: "Inter, sans-serif",
                fontWeight: isSelected ? 600 : 400,
                borderRadius: "20px",
                borderColor: isSelected ? "#1A1A1A" : "#C5BFBE",
                backgroundColor: isSelected ? "#1A1A1A" : "transparent",
                color: isSelected ? "#fff" : "#3D3634",
                cursor: "pointer",
                "&:hover": {
                  backgroundColor: isSelected ? "#3D3634" : "#F4F2F1",
                  borderColor: "#1A1A1A",
                },
                "& .MuiChip-label": { px: 1.5, py: 0.5 },
              }}
            />
          );
        })}
      </Box>

      {/* Other role text field */}
      {showOtherField && (
        <TextField
          label="Other Role"
          placeholder="Enter Role"
          value={otherRole}
          onChange={(e) => { isDirty.current = true; setOtherRole(e.target.value); }}
          fullWidth
          variant="outlined"
          size="small"
        />
      )}

      {/* Navigation */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 1 }}>
        <Button
          variant="outlined"
          onClick={goToPrevStep}
          disabled={isSaving}
          sx={{
            borderColor: "#C5BFBE",
            color: "#3D3634",
            borderRadius: "6px",
            px: 3,
            fontWeight: 600,
            letterSpacing: "0.5px",
            "&:hover": { borderColor: "#9E9E9E", backgroundColor: "#F4F2F1" },
          }}
        >
          BACK
        </Button>

        <Button
          variant="contained"
          onClick={handleNext}
          disabled={isSaving}
          sx={{
            backgroundColor: "#E84610",
            "&:hover": { backgroundColor: "#C73A0D" },
            "&:disabled": { backgroundColor: "#E8A090" },
            borderRadius: "6px",
            px: 3,
            fontWeight: 600,
            letterSpacing: "0.5px",
          }}
        >
          {isSaving ? "Saving..." : "NEXT"}
        </Button>
      </Box>
    </Box>
  );
};

export default Roles;
